import React, { createContext, useState, useEffect, ReactNode } from "react";
import { User, AuthState } from "../types";
import { authService } from "../services/authService";
import debugx from "debugx";

const debug = debugx("bikedash:auth");

export interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    isGuest: false,
    error: null,
  });

  useEffect(() => {
    // Subscribe to auth state changes from the service
    const unsubscribe = authService.onAuthStateChange((authState) => {
      debug.info("Auth state changed:", authState);
      setState({
        user: authState.user,
        token: null, // Using cookie auth now, not JWT
        isAuthenticated: authState.isAuthenticated,
        isLoading: authState.isLoading,
        isGuest: authState.isGuest,
        error: authState.error,
      });
    });

    // Check for existing session on mount
    const initAuth = async () => {
      try {
        // Try to restore from cached auth first (for offline)
        const cachedAuth = localStorage.getItem("bikedash_cached_auth");
        if (cachedAuth) {
          try {
            const cached = JSON.parse(cachedAuth);
            setState({
              user: cached.user,
              token: null,
              isAuthenticated: cached.isAuthenticated,
              isLoading: false,
              isGuest: cached.isGuest || false,
              error: null,
            });
            debug.info("Restored cached auth for user:", cached.user?.username);
          } catch (e) {
            debug.error("Failed to parse cached auth:", e);
          }
        }

        // Then validate with server
        await authService.validateSession();
      } catch (error) {
        debug.error("Failed to validate session:", error);
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();

    return () => {
      unsubscribe();
    };
  }, []);

  const login = async (username: string, password: string) => {
    // For popup auth, we just open the popup
    // The auth state will be updated via broadcast channel
    authService.openAuthPopup("login");
    debug.info("Opening auth popup for login");
  };

  const logout = async () => {
    await authService.logout();
    // Auth state will be updated by the service
    debug.info("User logged out");
  };

  const refreshToken = async () => {
    try {
      const token = state.token;
      if (!token) throw new Error("No token to refresh");

      const newToken = await authService.refreshToken(token);
      localStorage.setItem("authToken", newToken);
      setState((prev) => ({ ...prev, token: newToken }));
      debug.info("Token refreshed");
    } catch (error) {
      debug.error("Token refresh failed:", error);
      await logout();
      throw error;
    }
  };

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
